import React, { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import taskService, { PickableUser } from '../../services/taskService';
import referenceFileService, { ReferenceFile } from '../../services/referenceFileService';
import UserPicker from './UserPicker';
import { NEXT_STATUS, STATUS_LABEL } from './TaskWorkflowPanel';
import '../../assets/styles/Workflow.css';

interface SubtaskDetailModalProps {
  // A WorkflowTask (see services/taskService.ts) - only id, title, kind,
  // status, assignee_id, assignee_name, created_by_name are relied on here.
  task: any;
  projectSummary: string;
  // Caller decides this - true if the current user is this task's
  // assignee, its creator, or admin.
  canEdit: boolean;
  /** Project's own due date (YYYY-MM-DD) - a subtask's due date can never run later than this. */
  maxDueDate?: string;
  onClose: () => void;
  /** Passed the freshly-saved task so the caller can update its list
   * immediately, instead of only relying on a full reload - this project's
   * by-project fetch is measurably slow, so waiting on it alone made a
   * successful save look like it hadn't taken effect for several seconds. */
  onUpdated: (updated?: any) => void;
}

// Same exclusion AddSubtaskModal uses for the initial assignee pick - admin
// and Business Development never do subtask-level work, so reassigning to
// either doesn't make sense here either.
const SUBTASK_ASSIGNEE_EXCLUDE_ROLES = ['admin', 'Business Development'];

// QA is the only kind with a second possible outcome once In progress -
// Failed, alongside the happy-path QA Complete already in NEXT_STATUS.
// Kept local to this modal rather than reshaping the shared NEXT_STATUS
// export, since every other caller of that constant (Overview's one-click
// toggle, AssigneeTree, the dead TaskWorkflowPanel) only ever wants the
// single happy-path next status, never a branch to choose from.
const extraNextStatuses = (task: any): string[] =>
  task.kind === 'qa' && task.status === 'in_progress' ? ['failed'] : [];

// Scoped-down subtask detail modal. The prototype this mirrors also lets you
// edit assignee, time logged, and notes from here; cc_fdc_task has no notes/
// minutes columns, but title/requirements/priority/due date/assignee are all
// real columns now editable via FDCTaskService.edit_subtask.
const SubtaskDetailModal: React.FC<SubtaskDetailModalProps> = ({ task, projectSummary, canEdit, maxDueDate, onClose, onUpdated }) => {
  const nextStatus: string | undefined = NEXT_STATUS[task.kind]?.[task.status];
  const nextStatusOptions = [nextStatus, ...extraNextStatuses(task)].filter(Boolean) as string[];

  const [selectedStatus, setSelectedStatus] = useState<string>(task.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sample/De-dup/Other files uploaded when this subtask was created -
  // scoped to this subtask alone, never the project's general file list
  // (backend 403s anyone who isn't on this subtask, so an empty result here
  // can also just mean "none were attached", not necessarily a permission gap).
  const [referenceFiles, setReferenceFiles] = useState<ReferenceFile[]>([]);
  useEffect(() => {
    let active = true;
    referenceFileService.listForTask(task.id).then((files) => {
      if (active) setReferenceFiles(files);
    }).catch(() => { if (active) setReferenceFiles([]); });
    return () => { active = false; };
  }, [task.id]);

  const CATEGORY_LABEL: Record<string, string> = { sample: 'Sample File', dedup: 'De-duplication File', other: 'Other Reference File' };

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editAssigneeId, setEditAssigneeId] = useState<number | null>(null);
  const [editAssigneeUser, setEditAssigneeUser] = useState<PickableUser | null>(null);
  const [editRequirements, setEditRequirements] = useState('');
  const [editPriority, setEditPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [editDueDate, setEditDueDate] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    taskService.getUsersByIds([task.assignee_id]).then((users) => {
      if (active) setEditAssigneeUser(users[0] || null);
    }).catch(() => { if (active) setEditAssigneeUser(null); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.assignee_id]);

  const startEdit = () => {
    setEditTitle(task.title || '');
    setEditAssigneeId(task.assignee_id);
    setEditRequirements(task.requirements || '');
    setEditPriority((task.priority as 'low' | 'medium' | 'high') || 'medium');
    setEditDueDate(task.due_date ? String(task.due_date).slice(0, 10) : '');
    setEditError(null);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!editAssigneeId) {
      setEditError('Pick an assignee for the subtask');
      return;
    }
    setEditError(null);
    setSavingEdit(true);
    try {
      const updated = await taskService.editSubtask({
        task_id: task.id,
        assignee_id: editAssigneeId,
        title: editTitle || undefined,
        requirements: editRequirements || undefined,
        priority: editPriority,
        due_date: editDueDate || undefined,
      });
      onUpdated(updated);
      onClose();
    } catch (e: any) {
      setEditError((e?.response?.data?.message || e?.response?.message) || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSaveStatus = async () => {
    if (selectedStatus === task.status) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await taskService.updateTaskStatus(task.id, selectedStatus);
      onUpdated(updated);
      onClose();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const titleText = task.title || (task.kind === 'qa' ? 'QA review' : task.kind === 'deliver' ? 'Deliver to customer' : task.kind === 'other' ? 'Other subtask' : 'Build subtask');

  return (
    <div className="wf-overlay">
      <div className="wf-modal">
        <div className="wf-modal-head">
          <h2 style={{ fontSize: 17 }}>
            {task.kind === 'qa' ? '🔍 ' : task.kind === 'deliver' ? '📦 ' : task.kind === 'other' ? '📌 ' : ''}
            {titleText}
          </h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {canEdit && !editing && (
              <button className="wf-btn" onClick={startEdit}>✎ Edit</button>
            )}
            <button className="wf-iconbtn" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="wf-modal-body">
          {editing ? (
            <div className="wf-stack wf-form">
              {editError && <div style={{ color: '#ef4444', fontSize: 13 }}>{editError}</div>}
              <div>
                <label className="wf-field-label">Subtask title</label>
                <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
              </div>
              <div>
                <label className="wf-field-label">Assign to<span style={{ color: '#ef4444' }}> *</span></label>
                <UserPicker
                  excludeRoles={SUBTASK_ASSIGNEE_EXCLUDE_ROLES}
                  value={editAssigneeId}
                  initialUser={editAssigneeUser}
                  onChange={(id) => setEditAssigneeId(id)}
                  placeholder="Assign to..."
                />
              </div>
              <div className="wf-grid2">
                <div>
                  <label className="wf-field-label">Priority</label>
                  <select value={editPriority} onChange={(e) => setEditPriority(e.target.value as 'low' | 'medium' | 'high')}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="wf-field-label">Due date (SLA)</label>
                  <input
                    type="date"
                    max={maxDueDate}
                    value={editDueDate}
                    onChange={(e) => {
                      if (maxDueDate && e.target.value > maxDueDate) return;
                      setEditDueDate(e.target.value);
                    }}
                  />
                  {maxDueDate && <div className="wf-field-hint">Can't run later than the project's own due date ({maxDueDate}).</div>}
                </div>
              </div>
              <div>
                <label className="wf-field-label">Additional Notes</label>
                <div className="wf-requirements-editor">
                  <CKEditor
                    editor={ClassicEditor as any}
                    data={editRequirements}
                    onChange={(_, editor) => setEditRequirements(editor.getData())}
                    config={{ placeholder: 'Instructions for whoever picks this up...' }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="wf-stack wf-form">
              <div className="wf-detail-grid">
                <div>
                  <div className="wf-k">Project</div>
                  <div className="wf-v">{projectSummary}</div>
                </div>
                <div>
                  <div className="wf-k">Assignee</div>
                  <div className="wf-v">{task.assignee_name || 'Unassigned'}</div>
                </div>
                <div>
                  <div className="wf-k">Created by</div>
                  <div className="wf-v">{task.created_by_name || 'Unknown'}</div>
                </div>
                <div>
                  <div className="wf-k">Priority</div>
                  <div className="wf-v" style={{ textTransform: 'capitalize' }}>{task.priority || 'medium'}</div>
                </div>
                <div>
                  <div className="wf-k">SLA</div>
                  <div className="wf-v">{task.sla_label || '—'}</div>
                </div>
              </div>

              {task.requirements && (
                <div>
                  <div className="wf-field-label">Additional Notes</div>
                  <div style={{ fontSize: 13 }} dangerouslySetInnerHTML={{ __html: task.requirements }} />
                </div>
              )}

              {referenceFiles.length > 0 && (
                <div>
                  <div className="wf-field-label">Input Files</div>
                  {referenceFiles.map((f) => (
                    <div key={f.id} className="wf-filerow">
                      <span className="wf-chip2 wf-chip2-neutral">{f.category ? CATEGORY_LABEL[f.category] || f.category : 'File'}</span>
                      <span className="wf-fname">{f.file_name}</span>
                      <button
                        type="button"
                        className="wf-btn wf-btn-sm"
                        onClick={() => referenceFileService.download(f.id, f.file_name)}
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {error && <div style={{ color: '#ef4444', fontSize: 13 }}>{error}</div>}

              <div>
                <label className="wf-field-label">Status</label>
                {canEdit && nextStatusOptions.length > 0 ? (
                  <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                    <option value={task.status}>{STATUS_LABEL[task.status] || task.status}</option>
                    {nextStatusOptions.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
                    ))}
                  </select>
                ) : (
                  <div>
                    <span className={`wf-status-chip wf-status-${task.status}`}>{STATUS_LABEL[task.status] || task.status}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="wf-modal-foot">
          {editing ? (
            <>
              <button className="wf-btn" onClick={() => setEditing(false)} disabled={savingEdit}>Cancel</button>
              <button className="wf-btn wf-btn-primary" disabled={savingEdit} onClick={saveEdit}>
                {savingEdit ? 'Saving...' : 'Save changes'}
              </button>
            </>
          ) : (
            <>
              <button className="wf-btn" onClick={onClose}>{canEdit && nextStatusOptions.length > 0 ? 'Cancel' : 'Close'}</button>
              {canEdit && nextStatusOptions.length > 0 && (
                <button className="wf-btn wf-btn-primary" disabled={saving || selectedStatus === task.status} onClick={handleSaveStatus}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubtaskDetailModal;
